import { CABIN_CLASS } from '@web-platform/shared-universal-constants/src/cabinClass';
import { TRIP_TYPES } from '@web-platform/shared-universal-constants/src/tripTypes';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';

import {
  asEnum,
  buildCabinClass,
  buildDate,
  buildLocation,
  buildPassengerGroup,
} from './commonDimensions';

import type {
  CombinedSearch,
  FlightSearch,
  FlightSearchLeg,
  Search,
} from '@skyscanner-internal/schemas-types/clients.pb';
import type {
  BusinessDomain,
  DateTime as DateTimeSchema,
  Location,
  LocationAttributeLocationAttributeEncoding,
} from '@skyscanner-internal/schemas-types/commons.pb';
import type { FlightSearchKind } from '@skyscanner-internal/schemas-types/flights_search.pb';
import type { CabinClass as CabinClassConst } from '@web-platform/shared-universal-constants/src/cabinClass';
import type { DateTime } from '@web-platform/shared-universal-types/src/localisation';

export type SearchDimensions = {
  businessDomain?: 'COMBINED_SEARCH' | 'FLIGHT';
  eventName: string;
  previousEventId: string | null;
  commonSearchData: {
    adults: number;
    alternativeDestinations: boolean;
    alternativeOrigins: boolean;
    cabinClass: string;
    children: number;
    destinationCodeLeg1: string | undefined;
    href: string | undefined;
    inboundDate: DateTime;
    infants: number;
    isCabinBagRequired: boolean;
    isCheckedBagRequired: boolean;
    locale: string;
    nonAdultAges: number[];
    originCodeLeg1: string | undefined;
    outboundDate: DateTime;
    preferDirects: boolean;
    legs?: Array<{ origin: string | undefined; destination: string | undefined; date: DateTime }>;
    requestId: string;
    searchEventGuid: string;
    tripType: string;
    viewGuid: string;
  };
};

type FlightSearchParams = {
  adults: number;
  alternativeDestinations: boolean;
  alternativeOrigins: boolean;
  cabinClass: string;
  children: number;
  destination: string | undefined;
  eventName: string;
  href: string | undefined;
  inboundDate: DateTime;
  infants: number;
  isCabinBagRequired: boolean;
  isCheckedBagRequired: boolean;
  legs?: Array<{ origin: string | undefined; destination: string | undefined; date: DateTime }>;
  locale: string;
  nonAdultAges: number[];
  origin: string | undefined;
  outboundDate: DateTime;
  preferDirects: boolean;
  tripType: string;
};

const LEG_ENCODING = asEnum<LocationAttributeLocationAttributeEncoding>('SS_GEO1_ID');

const isDateTime = (value: unknown): value is DateTime =>
  (typeof value === 'string' && value.length > 0) || value instanceof Date;

const isCabinClass = (value: unknown): value is CabinClassConst =>
  typeof value === 'string' && Object.keys(CABIN_CLASS).includes(value);

const bookingPanelModalOpened = (urlString: string | undefined): boolean => {
  try {
    const parsedUrl = new URL(urlString || '');
    return Boolean(parsedUrl.hash && parsedUrl.hash.startsWith('#/details'));
  } catch (e) {
    return false;
  }
};

const buildLeg = ({
  date,
  destination,
  locale,
  origin,
}: {
  date: DateTime | undefined;
  destination: string | undefined;
  locale: string;
  origin: string | undefined;
}): FlightSearchLeg => {
  const leg: FlightSearchLeg = {
    origin: buildLocation(origin, locale, LEG_ENCODING),
    destination: buildLocation(destination, locale, LEG_ENCODING),
  };
  if (isDateTime(date)) {
    leg.date = buildDate(date);
  }
  return leg;
};

const buildSearchObject = ({
  adults,
  alternativeDestinations,
  alternativeOrigins,
  cabinClass,
  children,
  destination,
  eventName,
  href,
  inboundDate,
  infants,
  isCabinBagRequired,
  isCheckedBagRequired,
  legs,
  locale,
  nonAdultAges,
  origin,
  outboundDate,
  preferDirects,
  tripType,
}: FlightSearchParams): FlightSearch => {
  const baseFlightSearch: FlightSearch = {
    isOriginNearbyAirportSelected: alternativeOrigins || false,
    isDestinationNearbyAirportSelected: alternativeDestinations || false,
    passengerGroup: buildPassengerGroup({ adults, children, infants, nonAdultAges }),
    searchKind:
      eventName === ANALYTICS_EVENTS.FUNNEL.COMBINED_EXPLORE.NAME
        ? asEnum<FlightSearchKind>('NOT_FULLY_QUALIFIED')
        : asEnum<FlightSearchKind>('FULLY_QUALIFIED'),
    ...(isCabinClass(cabinClass) ? { cabinClass: buildCabinClass(cabinClass) } : undefined),
    ...(preferDirects ? { isDirectOnlySelected: preferDirects } : undefined),
  };

  // only set TCS baggage fare attributes on search event when booking panel not opened
  if (!bookingPanelModalOpened(href)) {
    baseFlightSearch.isCabinBagRequired = isCabinBagRequired || false;
    baseFlightSearch.isCheckedBagRequired = isCheckedBagRequired || false;
  }

  if (tripType === TRIP_TYPES.ONE_WAY) {
    return Object.assign(baseFlightSearch, {
      oneWay: {
        leg: buildLeg({ origin, destination, date: outboundDate, locale }),
      },
    });
  }
  if (tripType === TRIP_TYPES.RETURN) {
    return Object.assign(baseFlightSearch, {
      return: {
        leg1: buildLeg({ origin, destination, date: outboundDate, locale }),
        leg2: buildLeg({ origin: destination, destination: origin, date: inboundDate, locale }),
      },
    });
  }
  if (tripType === TRIP_TYPES.MULTI_DESTINATION && legs?.length) {
    return Object.assign(baseFlightSearch, {
      multiCity: {
        legs: legs.map((leg) =>
          buildLeg({ origin: leg.origin, destination: leg.destination, date: leg.date, locale }),
        ),
      },
    });
  }

  return baseFlightSearch;
};

const buildCombinedSearchObject = (
  args: Parameters<typeof buildSearchObject>[0],
): CombinedSearch => ({
  flightSearch: buildSearchObject(args),
});

const buildTripOverview = ({
  destinationCodeLeg1,
  inboundDate,
  legs,
  locale,
  originCodeLeg1,
  outboundDate,
  tripType,
}: {
  destinationCodeLeg1: string | undefined;
  inboundDate: DateTime;
  legs?: Array<{ origin: string | undefined; destination: string | undefined; date: DateTime }>;
  locale: string;
  originCodeLeg1: string | undefined;
  outboundDate: DateTime;
  tripType: string;
}): {
  origin: Location;
  destination: Location;
  startDate?: DateTimeSchema;
  endDate?: DateTimeSchema;
} => {
  if (tripType === TRIP_TYPES.MULTI_DESTINATION && legs?.length) {
    const firstLeg = legs[0];
    const lastLeg = legs.length > 1 ? legs[legs.length - 1] : undefined;
    return {
      origin: buildLocation(firstLeg.origin, locale, LEG_ENCODING),
      destination: buildLocation(
        lastLeg ? lastLeg.destination : firstLeg.destination,
        locale,
        LEG_ENCODING,
      ),
      ...(isDateTime(firstLeg.date) ? { startDate: buildDate(firstLeg.date) } : {}),
      ...(lastLeg && isDateTime(lastLeg.date) ? { endDate: buildDate(lastLeg.date) } : {}),
    };
  }

  const tripOverview: {
    origin: Location;
    destination: Location;
    startDate?: DateTimeSchema;
    endDate?: DateTimeSchema;
  } = {
    origin: buildLocation(originCodeLeg1, locale, LEG_ENCODING),
    destination: buildLocation(destinationCodeLeg1, locale, LEG_ENCODING),
  };

  if (isDateTime(outboundDate)) {
    tripOverview.startDate = buildDate(outboundDate);
  }
  if (tripType === TRIP_TYPES.RETURN && isDateTime(inboundDate)) {
    tripOverview.endDate = buildDate(inboundDate);
  }

  return tripOverview;
};

export const getSearch = ({
  businessDomain: businessDomainParam = 'COMBINED_SEARCH',
  commonSearchData,
  eventName,
  previousEventId,
}: SearchDimensions): Search => {
  const {
    adults,
    alternativeDestinations,
    alternativeOrigins,
    cabinClass,
    children,
    destinationCodeLeg1,
    href,
    inboundDate,
    infants,
    isCabinBagRequired,
    isCheckedBagRequired,
    legs,
    locale,
    nonAdultAges,
    originCodeLeg1,
    outboundDate,
    preferDirects,
    requestId,
    searchEventGuid,
    tripType,
    viewGuid,
  } = commonSearchData;
  // Reuse searchEventGuid id as view id if viewGuid is missing as they have a 1-to-1 relation
  const viewId = viewGuid || searchEventGuid;

  const flightSearchArgs = {
    adults,
    alternativeDestinations,
    alternativeOrigins,
    cabinClass,
    children,
    destination: destinationCodeLeg1,
    eventName,
    href,
    inboundDate,
    infants,
    isCabinBagRequired,
    isCheckedBagRequired,
    legs,
    locale,
    nonAdultAges,
    origin: originCodeLeg1,
    outboundDate,
    preferDirects,
    tripType,
  };

  const search: Search = {
    businessDomain: asEnum<BusinessDomain>(businessDomainParam),
    ...(businessDomainParam === 'FLIGHT'
      ? { flightSearch: buildSearchObject(flightSearchArgs) }
      : { combinedSearch: buildCombinedSearchObject(flightSearchArgs) }),
    ...buildTripOverview({
      destinationCodeLeg1,
      inboundDate,
      legs,
      locale,
      originCodeLeg1,
      outboundDate,
      tripType,
    }),
  };

  if (viewId) {
    search.viewId = viewId;
  }
  if (requestId) {
    search.requestId = requestId;
  }
  if (eventName === ANALYTICS_EVENTS.FUNNEL.COMBINED_EXPLORE.NAME && previousEventId) {
    search.previousEventId = previousEventId;
  }

  return search;
};
